import { useState, useEffect, useCallback } from 'react';
import { getAdminOfferCredits } from '../../api/adminApi';
import { fmtIST } from '../../utils/dateFormat';

const cyan = '#00e5ff';
const green = '#66ff99';

const AdminOfferCredits = () => {
  const [rows, setRows] = useState([]);
  const [grandTotal, setGrandTotal] = useState(null);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    getAdminOfferCredits({ page, search: search || undefined })
      .then((res) => {
        setRows(res.data.rows || []);
        setGrandTotal(res.data.grandTotal ?? 0);
        setTotal(res.data.total || 0);
        setPages(res.data.pages || 1);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const handleClear = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  // Extract "who joined" username from comment: "Offer - Registration - @username"
  const whoJoined = (comments) => {
    const m = (comments || '').match(/@(\S+)$/);
    return m ? m[1] : '—';
  };

  return (
    <div>
      <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}>Offer Credits</h4>
      <div style={{ color: '#555', fontSize: 13, marginBottom: 18 }}>{total} total credits</div>

      {/* Grand total chip */}
      {grandTotal !== null && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 2, background: 'rgba(102,255,153,0.08)', border: '1px solid rgba(102,255,153,0.25)', borderRadius: 12, padding: '10px 20px' }}>
            <span style={{ color: '#555', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Offer Credits Paid Out</span>
            <span style={{ color: green, fontSize: 22, fontWeight: 800, fontFamily: 'monospace' }}>
              {grandTotal.toFixed(2)} <span style={{ fontSize: 13, color: '#888', fontWeight: 500 }}>USDT</span>
            </span>
          </div>
        </div>
      )}

      {/* Search */}
      <form onSubmit={handleSearch} style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search referrer by username or user code..."
          style={{ flex: 1, minWidth: 200, background: 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 8, padding: '9px 14px', color: '#fff', fontSize: 13, outline: 'none' }}
        />
        <button type="submit" style={{ background: cyan, border: 'none', color: '#000', borderRadius: 8, padding: '9px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          Search
        </button>
        {search && (
          <button type="button" onClick={handleClear} style={{ background: 'rgba(255,255,255,0.06)', border: 'none', color: '#888', borderRadius: 8, padding: '9px 14px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Clear
          </button>
        )}
      </form>

      {/* Table */}
      {!isMobile ? (
        <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(0,229,255,0.07)', borderBottom: '1px solid rgba(0,229,255,0.12)' }}>
                {['#', 'Date & Time', 'Referrer', 'Joined User', 'Offer Credit (USDT)'].map((h) => (
                  <th key={h} style={{ color: cyan, fontSize: 12, fontWeight: 600, padding: '12px 16px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} style={{ color: '#555', textAlign: 'center', padding: 40, fontSize: 13 }}>Loading...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5} style={{ color: '#555', textAlign: 'center', padding: 40, fontSize: 13 }}>No offer credits found</td></tr>
              ) : (
                rows.map((r, i) => (
                  <tr
                    key={r._id}
                    style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(0,229,255,0.03)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={{ color: '#555', fontSize: 12, padding: '12px 16px' }}>{(page - 1) * 20 + i + 1}</td>
                    <td style={{ color: '#888', fontSize: 12, padding: '12px 16px', whiteSpace: 'nowrap' }}>{fmtIST(r.createdAt)}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{r.referrerName}</div>
                      <div style={{ color: cyan, fontSize: 11, fontFamily: 'monospace' }}>@{r.referrerUsername}</div>
                    </td>
                    <td style={{ color: '#cc66ff', fontSize: 13, fontWeight: 600, padding: '12px 16px', fontFamily: 'monospace' }}>@{whoJoined(r.comments)}</td>
                    <td style={{ color: green, fontSize: 13, fontWeight: 700, padding: '12px 16px', fontFamily: 'monospace' }}>+{r.amount.toFixed(2)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {loading ? (
            <div style={{ color: '#555', textAlign: 'center', padding: 40 }}>Loading...</div>
          ) : rows.length === 0 ? (
            <div style={{ color: '#555', textAlign: 'center', padding: 40 }}>No offer credits found</div>
          ) : (
            rows.map((r) => (
              <div key={r._id} style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, padding: '14px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10, gap: 10 }}>
                  <div>
                    <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{r.referrerName}</div>
                    <div style={{ color: cyan, fontSize: 11, fontFamily: 'monospace' }}>@{r.referrerUsername}</div>
                  </div>
                  <div style={{ color: green, fontSize: 14, fontWeight: 800, fontFamily: 'monospace' }}>+{r.amount.toFixed(2)} USDT</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <div style={{ color: '#555', fontSize: 10 }}>Joined User</div>
                    <div style={{ color: '#cc66ff', fontSize: 12, fontFamily: 'monospace' }}>@{whoJoined(r.comments)}</div>
                  </div>
                  <div>
                    <div style={{ color: '#555', fontSize: 10 }}>Date</div>
                    <div style={{ color: '#888', fontSize: 11 }}>{fmtIST(r.createdAt)}</div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
          {Array.from({ length: Math.min(pages, 5) }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => setPage(p)}
              style={{
                background: p === page ? cyan : 'rgba(255,255,255,0.06)',
                border: 'none',
                color: p === page ? '#000' : '#888',
                borderRadius: 6,
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminOfferCredits;
